/**
 * MED-1127 — shared, adaptive "join your session" banner for participants.
 *
 * Extracted from the dashboard's original `StartingSoonBanner` so the same
 * prominent card renders on BOTH the refreshed participant dashboard and the
 * top of the pre-session reflection page, across BOTH the starting-soon and
 * live states. The component is deliberately "dumb": each surface computes the
 * normalized props from its own payload, and the banner only maps
 * (state × isLeading) → eyebrow + CTA label + destination.
 *
 * CTA matrix:
 *   starting_soon + participant → "Starting Soon" / "Enter waiting room" → waiting room
 *   starting_soon + leader      → "Starting Soon" / "Prepare to start"   → reflection/prep (MED-1045)
 *   live          + participant → "Live now"      / "Join session"       → waiting room
 *   live          + leader      → "Live now"      / "Rejoin session"      → leader active session (MED-1045/AC10)
 *
 * Leader safety (AC9): the leader is NEVER routed to the participant waiting
 * room. On the pre-session page the active leader is redirected to /prep before
 * this banner renders, so `isLeading` there is always false; the leader branch
 * matters on the dashboard (dropped-and-returned case).
 */
import { Box, Button, Card, Group, Text, ThemeIcon } from '@mantine/core';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Zap } from 'react-feather';

export type SessionStartBannerState = 'starting_soon' | 'live';

interface SessionStartBannerProps {
  /** Which timing window the session is in — selects the eyebrow + CTA. */
  state: SessionStartBannerState;
  /** Session title shown in the banner body. */
  title: string;
  /** Viewer is the active session leader (peer_leader confirmed/backup). */
  isLeading: boolean;
  /** Session id — keys the waiting-room / reflection routes. */
  sessionId: string;
  /**
   * Cohort id — required for the leader "Rejoin session" route
   * (`/participant/cohorts/:cohortId/sessions/:sessionId/active`). When absent
   * for a leader we fall back to the prep route, never the waiting room.
   */
  cohortId?: string | null;
}

interface BannerCopy {
  eyebrow: string;
  ctaLabel: string;
  ctaTo: string;
  timingLabel: string;
}

function resolveCopy({
  state,
  isLeading,
  sessionId,
  cohortId,
}: SessionStartBannerProps): BannerCopy {
  const waitingRoom = `/participant/sessions/${sessionId}/waiting-room`;
  const prep = `/participant/sessions/${sessionId}/reflection`;
  const leaderActive = cohortId
    ? `/participant/cohorts/${cohortId}/sessions/${sessionId}/active`
    : prep; // leader fallback — prep is leader-safe; never the waiting room.

  if (state === 'live') {
    return {
      eyebrow: 'Live now',
      timingLabel: 'Live',
      ctaLabel: isLeading ? 'Rejoin session' : 'Join session',
      ctaTo: isLeading ? leaderActive : waitingRoom,
    };
  }
  // starting_soon
  return {
    eyebrow: 'Starting Soon',
    timingLabel: 'Soon',
    ctaLabel: isLeading ? 'Prepare to start' : 'Enter waiting room',
    ctaTo: isLeading ? prep : waitingRoom,
  };
}

export function SessionStartBanner(props: SessionStartBannerProps) {
  const { title } = props;
  const { eyebrow, ctaLabel, ctaTo, timingLabel } = resolveCopy(props);

  return (
    <Card radius="md" mb="lg" padding="lg" style={{ backgroundColor: '#2D4739', border: 'none' }}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon size={44} radius="xl" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <Zap size={20} color="white" />
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text size="xs" fw={600} c="rgba(255,255,255,0.7)" tt="uppercase" mb={2}>
              {eyebrow}
            </Text>
            <Text fw={600} c="white" size="md" truncate>
              {title}
            </Text>
          </Box>
        </Group>
        <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Clock size={14} color="rgba(255,255,255,0.7)" />
            <Text size="sm" c="rgba(255,255,255,0.85)" fw={500}>
              {timingLabel}
            </Text>
          </Group>
          <Button
            component={Link}
            to={ctaTo}
            size="sm"
            rightSection={<ArrowRight size={14} />}
            style={{ backgroundColor: '#4A7C59', border: 'none' }}
            data-testid="session-start-banner-cta"
          >
            {ctaLabel}
          </Button>
        </Group>
      </Group>
    </Card>
  );
}

export default SessionStartBanner;
